import { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Lock, Mail, AlertCircle } from "lucide-react";
import { login, getToken } from "@/lib/adminApi";

export default function AdminLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        // Pre-fill from URL hint (e.g. ?email=)
        const params = new URLSearchParams(location.search);
        const e = params.get("email");
        if (e) setEmail(e);
    }, [location.search]);

    if (getToken()) return <Navigate to="/admin" replace />;

    const submit = async (ev) => {
        ev.preventDefault();
        if (!email || !password) return;
        setSubmitting(true);
        setError("");
        try {
            await login(email.trim(), password);
            toast.success("Welcome back", { description: "Studio admin unlocked." });
            navigate("/admin", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Invalid credentials";
            setError(detail);
            toast.error("Login failed", { description: detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="relative min-h-screen overflow-hidden"
            style={{ background: "var(--pg-bg)" }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(900px 500px at 18% 12%, rgba(11, 59, 58, 0.12) 0%, rgba(11, 59, 58, 0) 60%), radial-gradient(900px 500px at 82% 30%, rgba(246, 199, 137, 0.12) 0%, rgba(246, 199, 137, 0) 55%)",
                }}
            />
            <div className="pg-grid-overlay absolute inset-0 opacity-50" aria-hidden />

            <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-12 sm:px-6">
                <div className="mb-8 flex items-center gap-2">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
                        <rect x="1.5" y="1.5" width="25" height="25" rx="7" stroke="var(--pg-fg)" strokeOpacity="0.4" />
                        <path d="M9 19V9h5.4c2 0 3.3 1.2 3.3 3.1S16.4 15.2 14.4 15.2H11" stroke="var(--pg-fg)" strokeWidth="1.6" strokeLinecap="round" />
                        <circle cx="21" cy="21" r="1.5" fill="var(--pg-accent)" />
                    </svg>
                    <span className="font-display text-lg" style={{ color: "var(--pg-fg)" }}>
                        Pranto<span style={{ color: "var(--pg-accent)" }}>.</span>Gfx
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4" style={{ color: "var(--pg-accent)" }} />
                    <span
                        className="font-mono text-[11px] uppercase tracking-[0.22em]"
                        style={{ color: "var(--pg-fg-muted)" }}
                    >
                        Studio Admin · Authorized Only
                    </span>
                </div>
                <h1
                    className="font-display mt-4 text-4xl sm:text-5xl"
                    style={{ color: "var(--pg-fg)" }}
                    data-testid="admin-login-headline"
                >
                    Welcome back, <span className="pg-text-gold">Pranto</span>.
                </h1>
                <p className="mt-3 text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                    Sign in to manage inquiries, packages, and client project deliveries.
                </p>

                <form
                    onSubmit={submit}
                    className="mt-8 space-y-5 rounded-2xl border p-6 sm:p-7"
                    style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                    data-testid="admin-login-form"
                >
                    <div className="flex flex-col gap-1.5">
                        <label className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>Email</label>
                        <div className="relative">
                            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: "var(--pg-fg-muted)" }} />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@studio.com"
                                className="pg-input pl-10"
                                data-testid="admin-login-email"
                                autoComplete="username"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>Password</label>
                        <div className="relative">
                            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: "var(--pg-fg-muted)" }} />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••••••"
                                className="pg-input pl-10"
                                data-testid="admin-login-password"
                                autoComplete="current-password"
                            />
                        </div>
                    </div>
                    {error && (
                        <p
                            className="flex items-center gap-1.5 text-[12px]"
                            style={{ color: "#FF6B6B" }}
                            data-testid="admin-login-error"
                        >
                            <AlertCircle className="h-3 w-3" /> {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={submitting || !email || !password}
                        className="pg-btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="admin-login-submit"
                    >
                        {submitting ? "Signing in…" : "Sign In"}
                        {!submitting && <ArrowRight className="h-4 w-4" strokeWidth={1.6} />}
                    </button>
                </form>

                <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                    Pranto.Gfx Studio CMS · Secured by JWT
                </p>
            </div>
        </div>
    );
}
